var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import moment from 'moment-timezone';
import { useCallback } from 'react';
import { v4 as uuid } from 'uuid';
import { flowsApi } from 'apis/flows';
import { analytics } from 'app/utils/segment';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { getDateAsUTC } from 'utils/date';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { getUTCOffset } from '../../utils/date';
import { EditModal } from '../editModal/editModal';
// When duplicating schedules, every opening hours entries should
// be updated with following:
//  - the current date
//  - the current agent name
//  - a new id
//  - a new start date (if start_date is in the past)
//  - a new overridden_by id what has to be indentical,
//    with the new id of overridding item
function adjustOpeningHours(openingHours, agentName) {
    const newIds = Object.fromEntries(openingHours.map(({ id }) => [id, uuid()]));
    return openingHours
        .map((openingHour) => {
        var _a;
        if (openingHour.opening_hour_type === 'regular') {
            const dayOfWeek = (_a = openingHour.recurring) === null || _a === void 0 ? void 0 : _a.day_of_week;
            const today = moment().isoWeekday();
            const momentWeekday = moment()
                .isoWeekday(dayOfWeek)
                .startOf('day')
                .add(getUTCOffset(), 'minutes');
            return Object.assign(Object.assign({}, openingHour), { created_at: moment().toISOString(), created_by: agentName, id: newIds[openingHour.id], start_date: today <= dayOfWeek
                    ? getDateAsUTC(momentWeekday).toISOString()
                    : getDateAsUTC(momentWeekday.add(1, 'weeks')).toISOString() });
        }
        return Object.assign(Object.assign({}, openingHour), { created_at: moment().toISOString(), created_by: agentName, id: newIds[openingHour.id], start_date: openingHour.next_date });
    })
        .map((openingHour) => (Object.assign(Object.assign({}, openingHour), (!!openingHour.overridden_by && {
        overridden_by: Object.assign(Object.assign({}, openingHour.overridden_by), { id: newIds[openingHour.overridden_by.id] }),
    }))));
}
export const DuplicateModal = ({ schedule: { id, name: scheduleName }, onClose, }) => {
    const { data: readFlowScheduleResponse } = flowsApi.useReadFlowScheduleQuery({
        id,
    });
    const [createSchedule, { isLoading }] = flowsApi.useCreateFlowScheduleMutation();
    const navigate = useAppNavigate();
    const notify = useNotifications();
    const { name: agentName } = useAppSelector((state) => state.settings.profile.profile);
    const handleSubmit = useCallback(({ name }) => __awaiter(void 0, void 0, void 0, function* () {
        analytics.businessHoursDuplicateScheduleFormSubmitted();
        if (readFlowScheduleResponse) {
            const res = yield createSchedule(Object.assign(Object.assign({}, readFlowScheduleResponse), { created_at: moment.utc().toISOString(), name, opening_hours: adjustOpeningHours(readFlowScheduleResponse.opening_hours, agentName) }));
            if ('data' in res) {
                notify({
                    title: 'Schedule duplicated!',
                    time: 5000,
                }, NotificationType.Success);
                navigate({
                    path: '/settings/businesshours/:id',
                    params: {
                        path: {
                            id: res.data.id,
                        },
                    },
                });
            }
            else {
                notify({
                    title: 'An error occurred when trying to duplicate schedule.',
                }, NotificationType.Error);
            }
        }
        else {
            notify({
                title: 'An error occurred when trying to fetch current schedule.',
            }, NotificationType.Error);
        }
    }), [agentName, navigate, readFlowScheduleResponse, createSchedule, notify]);
    return (_jsx(EditModal, { confirmName: "Duplicate", isLoading: isLoading, name: `${scheduleName} (copy)`, onClose: () => {
            onClose();
            analytics.businessHoursDuplicateScheduleFormCancelled();
        }, onSubmit: handleSubmit, title: "Duplicate schedule" }));
};
